import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { getApiUrl } from '../config/api';

const HeaderPremium = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check if user is logged in by using cookies (session_token for OAuth, access_token for regular login)
    // Skip auth check on signin page to avoid showing logged-in state after logout
    if (location.pathname === '/signin') {
      setUser(null);
      setSubscription(null);
      setIsLoggedIn(false);
      return;
    }

    const fetchSubscription = async () => {
      try {
        const API = getApiUrl();
        const resp = await fetch(`${API}/payments/subscription/current`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (resp.ok && resp.status !== 204) {
          const text = await resp.text();
          setSubscription(text ? JSON.parse(text) : null);
        } else {
          setSubscription(null);
        }
      } catch (err) {
        console.error('Header subscription check failed:', err);
        setSubscription(null);
      }
    };

    const checkAuth = async () => {
      try {
        const API = getApiUrl();
        const accessToken = localStorage.getItem('access_token');
        console.log('Header auth check - current location:', location.pathname);

        const resp = await fetch(`${API}/auth/me`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          credentials: 'include', // Include cookies in the request
        });
        console.log('Header auth check response status:', resp.status);
        if (resp.ok) {
          const userData = await resp.json();
          console.log('Header auth check - user data:', userData);
          setUser(userData);
          setIsLoggedIn(true);
          fetchSubscription();
        } else {
          console.log('Header auth check - response not ok');
          setUser(null);
          setSubscription(null);
          setIsLoggedIn(false);
          localStorage.removeItem('access_token');
        }
      } catch (err) {
        console.error('Header auth check failed:', err);
        setUser(null);
        setSubscription(null);
        setIsLoggedIn(false);
        localStorage.removeItem('access_token');
      }
    };
    checkAuth();
  }, [location]);

  const handleLogout = async () => {
    console.log('Logging out...');
    // Clear local state immediately
    setUser(null);
    setIsLoggedIn(false);
    localStorage.removeItem('access_token');

    try {
      const API = getApiUrl();
      const resp = await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      console.log('Logout response status:', resp.status);
      if (resp.ok) {
        console.log('Logout successful - cookies cleared by backend');
      } else {
        console.error('Logout API returned non-ok status:', resp.status);
      }
    } catch (err) {
      console.error('Logout API call failed:', err);
    }

    // Redirect to signin page instead of reload to allow browser to process cookie clearing
    window.location.href = '/signin';
  };

  const navLinks = [
    { label: 'Equity Intelligence™', href: '/equity-intelligence' },
    { label: 'Sector Intelligence™', href: '/sector-intelligence' },
    { label: 'Legal Intelligence™', href: '/legal-intelligence' },
    { label: 'Professional Intelligence™', href: '/professional-intelligence' },
    { label: 'Civil Services Intelligence™', href: '/civil-services-intelligence' },
    ...(isLoggedIn ? [{ label: 'Profile', href: '/profile' }] : []),
    { label: 'Subscription', href: '/subscription' },
    ...((String(user?.role || '').trim().toUpperCase().replace(/^ROLE_/, '') === 'ADMIN_USER')
      ? [{ label: 'Admin', href: '/admin' }]
      : [])
  ];

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/98 backdrop-blur-xl border-b border-slate-200 shadow-sm' 
          : 'bg-white border-b border-slate-100'
      }`}
      data-testid="premium-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="/" 
            className="flex items-center shrink-0 pr-2"
            data-testid="header-logo"
          >
            <img 
              src="/logo.png" 
              alt="The B Tree" 
              className="h-16 w-auto"
            />
          </a>

          {/* Desktop Navigation with Underline Animation */}
          <nav className="hidden lg:flex items-center gap-x-2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="whitespace-nowrap text-[12px] xl:text-[13px] tracking-normal text-slate-900 hover:text-[#A77F16] transition-colors font-semibold relative group"
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#C8A84B] group-hover:w-full transition-all duration-300"></span>
              </a>
            ))}
          </nav>

          {/* User Dropdown / Sign In */}
          {isLoggedIn ? (
            <div className="hidden lg:flex items-center shrink-0" ref={dropdownRef}>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className="flex items-center gap-2 text-left leading-tight px-2 py-1.5 rounded-lg border border-slate-200 bg-white shadow-sm hover:bg-slate-50 hover:border-[#C8A84B]/60 transition-colors"
                  data-testid="profile-dropdown-trigger"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C8A84B] flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold text-slate-950 leading-tight truncate">
                      {user?.name || user?.email || 'User'}
                    </p>
                    <p className="text-[11px] font-semibold text-slate-600 leading-tight truncate">
                      {subscription?.plan_name || (subscription?.plan_type ? subscription.plan_type.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Free Plan')}
                    </p>
                  </div>
                  <svg className={`w-3.5 h-3.5 text-slate-600 transition-transform duration-200 shrink-0 ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                    {/* User info header */}
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-900">{user?.name || user?.email || 'User'}</p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{user?.email}</p>
                    </div>

                    {/* Subscription info */}
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Subscription</p>
                      <p className="text-sm font-semibold text-slate-800">
                        {subscription?.plan_name || (subscription?.plan_type ? subscription.plan_type.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Free Plan')}
                      </p>
                      {subscription?.end_date && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          Renews {new Date(subscription.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                      <button
                        onClick={() => { setDropdownOpen(false); navigate('/subscription'); }}
                        className="mt-2 text-xs text-[#C8A84B] hover:text-[#a8882b] font-medium"
                      >
                        Manage subscription →
                      </button>
                    </div>

                    {/* Actions */}
                    <div className="py-1">
                      <button
                        onClick={() => { setDropdownOpen(false); navigate('/profile'); }}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        data-testid="profile-link"
                      >
                        My Profile
                      </button>
                      <button
                        onClick={() => { setDropdownOpen(false); handleLogout(); }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        data-testid="signout-link"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <button
              onClick={() => navigate('/signin')}
              className="hidden lg:inline-flex text-[14px] tracking-normal text-slate-900 hover:text-[#A77F16] transition-colors font-semibold relative group shrink-0 pl-2"
              data-testid="signin-link"
            >
              Sign In
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#C8A84B] group-hover:w-full transition-all duration-300"></span>
            </button>
          )}

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileOpen((prev) => !prev)}
            className="md:hidden text-slate-900 p-2"
            aria-expanded={mobileOpen}
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
            </svg>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-xl">
          <div className="px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => {
                  setMobileOpen(false);
                  navigate(link.href);
                }}
                className="w-full text-left rounded-3xl py-3 px-4 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition"
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}

            <div className="border-t border-slate-200 pt-4 space-y-1">
              {isLoggedIn ? (
                <>
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl mb-2">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C8A84B] flex items-center justify-center text-white text-sm font-bold shrink-0">
                      {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{user?.name || user?.email || 'User'}</p>
                      <p className="text-xs text-slate-500">
                        {subscription?.plan_name || (subscription?.plan_type ? subscription.plan_type.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Free Plan')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setMobileOpen(false); navigate('/profile'); }}
                    className="w-full text-left rounded-3xl py-3 px-4 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition"
                    data-testid="mobile-profile-link"
                  >
                    My Profile
                  </button>
                  <button
                    onClick={() => { setMobileOpen(false); navigate('/subscription'); }}
                    className="w-full text-left rounded-3xl py-3 px-4 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition"
                  >
                    Manage Subscription
                  </button>
                  <button
                    onClick={() => { setMobileOpen(false); handleLogout(); }}
                    className="w-full text-left rounded-3xl py-3 px-4 text-sm font-medium text-red-600 hover:bg-red-50 transition"
                    data-testid="mobile-signout-link"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    navigate('/signin');
                  }}
                  className="w-full text-left rounded-3xl py-3 px-4 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition"
                  data-testid="mobile-signin-link"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default HeaderPremium;
